import type { ChoixDePriorite, DateAuPlusTot, Etat, Priorite, Situation } from '../api/types';
import { enFrancais, selonLaDateAuPlusTot } from '../datesEnMots';

/**
 * Où en est une bulle, en mots : ses états, son importance, sa situation, sa date au plus tôt ;
 * et le tour des états au clic sur le rond.
 */

/**
 * Les quatre états, tels qu'ils s'écrivent À L'ÉCRAN (table de traduction d'AGENTS.md).
 *
 * Le modèle dit `fait`, l'écran dit « acquis » — et il le dit **ici, une seule fois**. Cette
 * table était recopiée dans cinq composants : cinq endroits à retrouver le jour où un mot
 * change, et cinq occasions de laisser passer « terminé » quelque part.
 */
export const MOTS_ETAT: Record<Etat, string> = {
  a_faire: 'À faire',
  en_attente_confirmation: 'En instruction',
  fait: 'Acquis',
  // Ex « Sans objet » : une idée gardée, pas une impasse (ticket 18 de retours-testeurs).
  sans_objet: 'En réflexion',
};

/**
 * L'importance d'une bulle à l'écran (chantier `flottaison`, décision d'Igor du 2026-10-01) :
 * réglée à la main, jamais calculée. « Quand tu veux » est l'importance la plus basse ; une
 * bulle sur laquelle rien n'est réglé est « non réglée ». Plus d'« Auto », plus d'« En
 * attente » comme priorité.
 */
export const MOTS_PRIORITE: Record<Priorite, string> = {
  urgent: 'Urgent',
  important: 'Important',
  plus_tard: 'Quand tu veux',
};
export const NON_REGLEE = 'Non réglée';
export const IMPORTANCE = 'Importance';
export const IMPORTANCE_NON_REGLEE = 'Importance non réglée';

/**
 * Ce que chaque importance veut dire : la glose des intertitres de la Liste
 * (`intertitresDeLaListe.ts`). Une bulle acquise ou en réflexion n'a pas d'importance, et
 * garde son intertitre à elle, en dernier.
 */
export const GLOSE_DE_PRIORITE: Record<Priorite, string> = {
  urgent: 'À faire en priorité',
  important: 'À garder à l’œil',
  plus_tard: 'Pas prioritaire',
};
export const GLOSE_NON_REGLEE = 'Rien de réglé, ni sur elle ni au-dessus';
export const HORS_JEU = 'Acquis ou en réflexion';
export const GLOSE_HORS_JEU = 'Plus à faire pour l’instant';

/**
 * Les quatre crans de « Son importance », dans l'ordre du sélecteur, du plus bas au plus fort
 * (décision d'Igor du 2026-10-01) : « Non réglée » ramène à rien.
 */
export const CHOIX_DE_PRIORITE: [ChoixDePriorite | null, string][] = [
  ['plus_tard', MOTS_PRIORITE.plus_tard],
  [null, NON_REGLEE],
  ['important', MOTS_PRIORITE.important],
  ['urgent', MOTS_PRIORITE.urgent],
];

/**
 * La petite étiquette d'une bulle « En réflexion », sous son titre en Carte, en Liste et en
 * Hiérarchie, à la place de la ligne des réglages (ticket 18, décision d'Igor). C'est elle
 * qui la distingue d'une bulle « Rien dedans », qui porte le même bord en pointillé.
 */
export const IDEE = 'Idée';

/**
 * Une bulle terminale jamais creusée (« jamais ouverte » dans la SPEC), telle que l'écran la
 * nomme : la légende de la carte et ce que lit un lecteur d'écran. Son seul signe est un bord
 * en pointillé ; pour le reste elle a l'air aussi cliquable qu'une autre (retour 4 des
 * testeurs, ticket 03). Le même mot que la feuille qu'on trouve en y entrant (« Rien
 * dedans », `VueTableau.tsx`).
 */
export const RIEN_DEDANS = 'Rien dedans';

/**
 * Le tour des états au clic sur le rond, le même dans les trois vues. `sans_objet` (« En
 * réflexion ») n'y figure pas : garder une chose comme idée est une décision, pas un état
 * qu'on traverse en cliquant trois fois. Elle se choisit dans la fiche.
 */
export function etatSuivant(etat: Etat): Etat {
  switch (etat) {
    case 'a_faire':
      return 'en_attente_confirmation';
    case 'en_attente_confirmation':
      return 'fait';
    default:
      return 'a_faire';
  }
}

/**
 * Ce que la situation dit, en une phrase.
 *
 * Trois interdits tenus ici :
 *   · **aucun rouge, aucun reproche** — un délai dépassé est une information, pas un retard
 *     (principe 3) ;
 *   · aucun compteur de jours sur une échéance : seule l'attente du monde en porte un,
 *     parce que c'est elle qui appelle une relance ;
 *   · rien du tout pour une bulle ordinaire. Le silence est la situation la plus fréquente.
 */
export function motsDeSituation(situation: Situation): string | null {
  switch (situation.nom) {
    case 'a_verifier':
      return situation.joursDAttente === 1
        ? 'Tu as fait ta part. La réponse était attendue hier.'
        : `Tu as fait ta part. La réponse était attendue il y a ${situation.joursDAttente} jours.`;

    case 'en_attente':
      return situation.attenduLe
        ? `Tu as fait ta part. Le retour est attendu le ${enFrancais(situation.attenduLe)}.`
        : 'Tu as fait ta part. La balle est chez eux.';

    case 'acquis':
      return 'C’est derrière toi.';

    case 'sans_objet':
      return 'Une idée, pas encore une tâche : elle ne compte pas dans ce qui reste à faire et ne retient rien. Donne-lui le temps qu’elle prend, d’où, une date ou un délai : elle redevient une tâche.';

    default:
      return null;
  }
}

/**
 * Une bulle que rien ne date (chantier `flottaison`, décision d'Igor du 2026-10-01) : « Quand
 * tu veux » est devenu le nom de l'importance la plus basse, la date dit « pas de date ».
 */
export const PAS_DE_DATE = 'Pas de date';
export const PAS_ENCORE_CALCULABLE = 'pas encore calculable';

/**
 * La date au plus tot, en mots.
 *
 * Les deux reponses qui ne sont pas une date ne sont pas des echecs : « pas de date » est
 * la bonne reponse pour un projet que personne n'attend, et « pas encore calculable » est
 * plus honnete qu'un mardi invente. Une app qui affiche une date plausible faute de savoir
 * est menteuse — la seule chose dont elle ne se releve pas.
 */
export function motsDeDateAuPlusTot(date: DateAuPlusTot): string {
  return selonLaDateAuPlusTot(date, {
    date: (jour) => `Au plus tôt : ${enFrancais(jour)}`,
    quandTuVeux: PAS_DE_DATE,
    pasCalculable: 'Pas encore calculable',
  });
}
